import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from '../legal/LegalLayout';
import { PrivacyShieldIllustration } from '../legal/illustrations';
import { authHeader, isLoggedIn, getUser, clearSession } from '../auth/auth';

// ─────────────────────────────────────────────────────────────────────────────
// Delete your own profile. Mirrors the backend UserController::deleteUser:
//   DELETE /api/users/{id}  (auth required; self or admin)
//   body: { title, description }   — at least one is required
// The backend answers with a numeric "message":
//   0 → empty title/justification
//   1 → active events block the deletion
//   2 → deletion successful (status set to 2)
// ─────────────────────────────────────────────────────────────────────────────

const API = 'https://maineventbackend.seuytel.com/api/';

type Status = 'idle' | 'sending' | 'done' | 'error';

export default function DeleteProfile() {
    const user = getUser();
    const loggedIn = isLoggedIn();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [confirm, setConfirm] = useState(false);
    const [status, setStatus] = useState<Status>('idle');
    const [error, setError] = useState('');

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!user?.id) {
            setStatus('error');
            setError('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
            return;
        }
        if (!title.trim() && !description.trim()) {
            // Same rule the backend enforces (message 0).
            setStatus('error');
            setError('Bitte geben Sie einen Titel oder eine Begründung für die Löschung an.');
            return;
        }

        setStatus('sending');
        try {
            const res = await fetch(`${API}users/${user.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    ...authHeader(),
                },
                body: JSON.stringify({
                    title: title.trim(),
                    description: description.trim(),
                }),
            });

            if (res.status === 401) {
                setStatus('error');
                setError('Bitte melden Sie sich an, um Ihr Konto zu löschen.');
                return;
            }

            const data = await res.json().catch(() => ({}));
            const message = data?.message;

            if (res.ok && (message === 2 || data?.status === 'success')) {
                clearSession();
                setStatus('done');
                return;
            }

            setStatus('error');
            if (message === 0) {
                setError('Bitte geben Sie einen Titel oder eine Begründung für die Löschung an.');
            } else if (message === 1) {
                setError(
                    'Ihr Konto kann derzeit nicht gelöscht werden: Sie haben noch aktive Events mit zahlenden ' +
                        'Teilnehmern. Bitte schließen oder stornieren Sie diese Events (inkl. Rückerstattungen) und ' +
                        'versuchen Sie es anschließend erneut.'
                );
            } else {
                setError('Die Löschung konnte nicht durchgeführt werden. Bitte versuchen Sie es später erneut.');
            }
        } catch {
            setStatus('error');
            setError('Netzwerkfehler. Bitte versuchen Sie es später erneut.');
        }
    };

    return (
        <LegalLayout
            eyebrow="Konto"
            title="Profil löschen"
            intro="Sie können Ihr MainEvents-Konto selbst löschen. Bitte teilen Sie uns kurz den Grund mit – das hilft uns, die Plattform zu verbessern."
            art={<PrivacyShieldIllustration />}
        >
            {!loggedIn ? (
                <div className="se-alert se-alert-info">
                    Zum Löschen Ihres Kontos ist eine Anmeldung erforderlich. <Link to="/login">Jetzt anmelden</Link>.
                </div>
            ) : status === 'done' ? (
                <div className="se-alert se-alert-success">
                    <strong>Ihr Konto wurde zur Löschung markiert.</strong> Sie wurden abgemeldet. Ihre Daten werden
                    entfernt, sobald keine gesetzlichen Aufbewahrungspflichten mehr entgegenstehen.{' '}
                    <Link to="/events">Zur Startseite</Link>.
                </div>
            ) : (
                <>
                    <div className="se-alert se-alert-warn">
                        <strong>Achtung:</strong> Nach der Löschung ist Ihr Profil nicht mehr in der Suche auffindbar und
                        Sie können sich nicht mehr anmelden. Solange Sie noch aktive Events mit zahlenden Teilnehmern
                        haben, ist eine Löschung nicht möglich.
                    </div>

                    <form className="se-form-card" onSubmit={submit}>
                        <div className="se-field">
                            <label className="se-label" htmlFor="del-title">Titel</label>
                            <input
                                id="del-title"
                                className="se-input"
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="z. B. Ich nutze die App nicht mehr"
                                maxLength={120}
                            />
                        </div>

                        <div className="se-field">
                            <label className="se-label" htmlFor="del-desc">Begründung</label>
                            <textarea
                                id="del-desc"
                                className="se-input se-textarea"
                                rows={5}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Optional, wenn Sie oben einen Titel angegeben haben…"
                            />
                            <span className="se-help">Bitte geben Sie mindestens einen Titel oder eine Begründung an.</span>
                        </div>

                        <label className="se-check">
                            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
                            Ich möchte mein Konto endgültig löschen.
                        </label>

                        {status === 'error' && <div className="se-alert se-alert-error">{error}</div>}

                        <button
                            type="submit"
                            className="se-btn-danger"
                            disabled={status === 'sending' || !confirm}
                        >
                            {status === 'sending' ? 'Wird gelöscht…' : 'Konto endgültig löschen'}
                        </button>
                    </form>
                </>
            )}

            <p className="se-callout" style={{ marginTop: 24 }}>
                Lieber doch nicht löschen? Wenn Sie ein Problem mit einem anderen Nutzer oder Event haben, können Sie es{' '}
                <Link to="/profil-melden">melden</Link> oder unseren Support unter{' '}
                <a href="mailto:support@mainevents.seuytel.com">support@mainevents.seuytel.com</a> kontaktieren.
            </p>
        </LegalLayout>
    );
}
